import { DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/core';
import { colorChannel } from '@mui/system';
import { useQuery } from '@tanstack/react-query';
import { addWeeks, endOfDay, endOfWeek, fromUnixTime, getUnixTime, isToday, startOfDay, startOfWeek, subWeeks } from 'date-fns';
import { useRef, useState } from 'react';
import { useSession } from 'src/contexts/SessionContext';
import { Appointment } from 'src/models/appointment/_types';
import {
  createAppointment,
  fetchAppointment,
} from 'src/models/appointment/appointment';
import { fetchCalendar } from 'src/models/calendar/calendar';
import { fetchFbmResources } from 'src/models/fbm-resource/fbm-resource';
import { ScheduleItem } from 'src/models/schedule/_types';
import { fetchSchedule } from 'src/models/schedule/schedule';
import { ScheduledAppointment } from 'src/models/scheduled-appointment/_types';
import { fetchScheduledAppointment } from 'src/models/scheduled-appointment/scheduled-appointment';
import { fetchAllTrainers } from 'src/models/trainer/trainer';
import { getUserDisplayName } from 'src/models/user/utils';
import { ScheduledMembership } from 'src/models/visit/_types';
import { fetchScheduledMemberships } from 'src/models/visit/visit';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';
import { Identifier } from 'src/types/utilityTypes';

import { getCalendarHeaderTitle } from './calendarHeaderTitle';

export const FALLBACK_CALENDAR_EVENT_COLOR = '#4ff000';

export const resolveCalendarEventColor = (color?: string | null) =>
  color || FALLBACK_CALENDAR_EVENT_COLOR;

export const getCalendarEventColorChannel = (color?: string | null) =>
  colorChannel(resolveCalendarEventColor(color));

type Mode = 'timeGridWeek' | 'resourceTimeGridDay' | 'listWeek';

export interface CalendarResource {
  id: string;
  title: string;
  sortOrder: number | null;
}

export interface CalendarFilterParams {
  cancelled?: boolean;
  locationId?: Identifier | string; // Can be comma-separated for multiple locations
  noAvailabilitySchedules?: boolean;
  trainerId?: Identifier | string; // Can be comma-separated for multiple trainers
  noEmptyTrainer?: string;
  onlyAssets?: string;
  spotsRemaining?: number;
  serviceId?: Identifier | string; // Can be comma-separated for multiple services
  category?: Identifier | string; // Can be comma-separated for multiple categories
  resourceId?: Identifier | string; // Can be comma-separated for multiple booked assets
  trainerTag?: string | { values?: string[]; matchAll?: boolean }; // Staff member tags (string or TagsFilter object)
  showAttendees?: boolean; // Show attendees on list and print view
  // URL date params (unix timestamps as strings)
  start?: string;
  end?: string;
  // Callback when date changes
  onDateChange?: (start: number, end: number) => void;
}

export const useCalendar = (filterParams: CalendarFilterParams = {}) => {
  const { session, user, sessionTrainer } = useSession();
  const { canViewOthersScheduling } = usePermissionCheck();
  const calendarRef = useRef<any>(null);

  // Normalize filter values - treat "0" for trainerId and "false" for any param as blank
  const normalizeFilterValue = <T,>(value: T, key?: string): T | undefined => {
    if (value === undefined || value === null) return undefined;
    const strValue = String(value);
    if (strValue === 'false') return undefined;
    if (key === 'trainerId' && strValue === '0') return undefined;
    return value;
  };

  // Normalize tags filter value - convert { values: string[] } to comma-separated string
  const normalizeTagsValue = (value: unknown): string | undefined => {
    if (!value) return undefined;
    // Handle object format from TagsFilter: { values: string[], matchAll?: boolean }
    if (typeof value === 'object' && value !== null && 'values' in value) {
      const tagsObj = value as { values?: string[]; matchAll?: boolean };
      if (tagsObj.values && tagsObj.values.length > 0) {
        return tagsObj.values.join(',');
      }
      return undefined;
    }
    // Handle string format (already comma-separated or single tag)
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
    return undefined;
  };

  // Normalized filter params for API calls
  const normalizedFilters = {
    cancelled: normalizeFilterValue(filterParams.cancelled),
    locationId: normalizeFilterValue(filterParams.locationId),
    noAvailabilitySchedules: normalizeFilterValue(filterParams.noAvailabilitySchedules),
    trainerId: normalizeFilterValue(filterParams.trainerId, 'trainerId'),
    noEmptyTrainer: normalizeFilterValue(filterParams.noEmptyTrainer),
    onlyAssets: normalizeFilterValue(filterParams.onlyAssets),
    spotsRemaining: normalizeFilterValue(filterParams.spotsRemaining),
    serviceId: normalizeFilterValue(filterParams.serviceId),
    category: normalizeFilterValue(filterParams.category),
    resourceId: normalizeFilterValue(filterParams.resourceId),
    trainerTag: normalizeTagsValue(filterParams.trainerTag),
    showAttendees: normalizeFilterValue(filterParams.showAttendees),
  };

  // Initialize date from URL params if present
  const getInitialDate = (): Date => {
    if (filterParams.start) {
      const startTimestamp = parseInt(filterParams.start, 10);
      if (!isNaN(startTimestamp)) {
        return fromUnixTime(startTimestamp);
      }
    }
    return new Date();
  };

  const [date, setDate] = useState(getInitialDate);
  const [view, setViewState] = useState<Mode>(user.defaultCalendarView as Mode);
  const [calendarTitle, setCalendarTitle] = useState('');

  const dateIsToday = isToday(date);

  const handleDatesSet = (info: DatesSetArg) => {
    const calendarApi = calendarRef.current?.getApi();
    const nextDate = calendarApi?.getDate() ?? info.view.currentStart;

    setDate(nextDate);
    setCalendarTitle(info.view.title);
    setViewState(info.view.type as Mode);

    if (filterParams.onDateChange) {
      filterParams.onDateChange(getUnixTime(info.start), getUnixTime(info.end));
    }
  };

  const next = () => {
    calendarRef.current?.getApi()?.next();
  };
  const prev = () => {
    calendarRef.current?.getApi()?.prev();
  };

  const setView = (newMode: Mode) => {
    calendarRef.current?.getApi()?.changeView(newMode);
    setViewState(newMode);
  };
  const setDateToToday = () => {
    calendarRef.current?.getApi()?.today();
  };

  const goToDate = (targetDate: Date) => {
    calendarRef.current?.getApi()?.gotoDate(targetDate);
  };

  // Force FullCalendar to re-render events and resources
  const refreshCalendar = async () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {

      // refetchResources is only available with FullCalendar Scheduler
      if (typeof calendarApi.refetchResources === 'function') {
        await calendarApi.refetchResources();
      }

      await calendarApi.refetchEvents();
    }
  };

  const title = getCalendarHeaderTitle(view, date, calendarTitle);

  const formattedDate = date?.toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  });

  // Fetch all trainers first (includes tags for filtering)
  const { data: fetchedTrainers = [], isLoading: isLoadingTrainers } = useQuery({
    queryKey: ['trainers', 'all'],
    queryFn: () => fetchAllTrainers(),
  });

  // Merge sessionTrainer (platform owner) into trainers list if not already present
  // This ensures the head trainer is always available in the calendar resources
  const trainers = (() => {
    if (!sessionTrainer) return fetchedTrainers;

    const sessionTrainerId = sessionTrainer.trainerId || sessionTrainer.id;
    const isAlreadyIncluded = fetchedTrainers.some(
      (t) => t.trainerId === sessionTrainerId
    );

    if (isAlreadyIncluded) return fetchedTrainers;

    // Create a trainer-like object from sessionTrainer
    const sessionTrainerAsTrainer = {
      trainerId: sessionTrainerId,
      name: getUserDisplayName(sessionTrainer)?.toString() || '',
      tags: [] as string[],
    };

    return [sessionTrainerAsTrainer, ...fetchedTrainers];
  })();

  // Compute effective trainer IDs for API calls
  // If trainerTag is set, filter trainers by tag and use their IDs
  // Otherwise use the trainerId from the filter
  const getEffectiveTrainerIds = (): string | undefined => {
    if (normalizedFilters.trainerTag && trainers.length > 0) {
      // Parse comma-separated tags
      const selectedTags = String(normalizedFilters.trainerTag).split(',').map(t => t.trim());

      // Filter trainers that have any of the selected tags
      const matchingTrainers = trainers.filter(trainer =>
        trainer.tags?.some(tag => selectedTags.includes(tag))
      );

      if (matchingTrainers.length > 0) {
        return matchingTrainers.map(t => t.trainerId).join(',');
      }
      // No matching trainers - return empty to show no events
      return '';
    }

    // No tag filter, use trainerId from filter (with fallback to current user)
    return normalizedFilters.trainerId ? String(normalizedFilters.trainerId) : undefined;
  };

  const effectiveTrainerIds = getEffectiveTrainerIds();

  const { data: events, isLoading: isLoadingEvents } = useQuery({
    queryKey: [
      'calendar-events',
      normalizedFilters.cancelled,
      normalizedFilters.locationId,
      normalizedFilters.noAvailabilitySchedules,
      effectiveTrainerIds,
      normalizedFilters.spotsRemaining,
      normalizedFilters.serviceId,
      normalizedFilters.category,
      normalizedFilters.resourceId,
      normalizedFilters.trainerTag,
      date,
    ],
    queryFn: () => {
      const baseDate = date || new Date();
      return fetchCalendar({
        type: 'appointments',
        start: Math.floor(subWeeks(baseDate, 1).getTime() / 1000),
        end: Math.floor(addWeeks(baseDate, 1).getTime() / 1000),
        cancelled: normalizedFilters.cancelled,
        location: normalizedFilters.locationId,
        noAvailabilitySchedules: normalizedFilters.noAvailabilitySchedules,
        userId: effectiveTrainerIds,
        spotsRemaining: normalizedFilters.spotsRemaining,
        serviceId: normalizedFilters.serviceId,
        category: normalizedFilters.category,
        resourceId: normalizedFilters.resourceId,
        trainerTag: normalizedFilters.trainerTag,
        currentUserId: user.id,
      });
    },
    staleTime: 0,
  });

  // Note: URL params may store booleans as strings, so check explicitly for 'true'
  const shouldHideSchedules = String(normalizedFilters.noAvailabilitySchedules) === 'true';

  // Always fetch schedules - we filter them client-side via shouldHideSchedules
  const { data: schedules, isLoading: isLoadingSchedules } = useQuery({
    queryKey: [
      'calendar-schedules',
      normalizedFilters.cancelled,
      normalizedFilters.locationId,
      effectiveTrainerIds,
      normalizedFilters.spotsRemaining,
      normalizedFilters.serviceId,
      normalizedFilters.category,
      normalizedFilters.resourceId,
      normalizedFilters.trainerTag,
      date,
    ],
    queryFn: () => {
      const baseDate = date || new Date();
      return fetchCalendar({
        type: 'schedules',
        start: Math.floor(subWeeks(baseDate, 1).getTime() / 1000),
        end: Math.floor(addWeeks(baseDate, 1).getTime() / 1000),
        cancelled: normalizedFilters.cancelled,
        location: normalizedFilters.locationId,
        userId: effectiveTrainerIds,
        spotsRemaining: normalizedFilters.spotsRemaining,
        serviceId: normalizedFilters.serviceId,
        category: normalizedFilters.category,
        resourceId: normalizedFilters.resourceId,
        trainerTag: normalizedFilters.trainerTag,
        currentUserId: user.id,
      });
    },
    staleTime: 0,
  });

  // Fetch FBM resources (assets like rooms, equipment)
  const { data: fbmResourcesData, isLoading: isLoadingFbmResources } = useQuery({
    queryKey: ['fbm-resources', normalizedFilters.locationId],
    queryFn: () =>
      fetchFbmResources({
        fetchAll: true,
        sort: null,
        sortDir: null,
        search: null,
        filters: {
          locationId: normalizedFilters.locationId,
        },
      }),
    staleTime: 60000,
  });

  const fbmResources = fbmResourcesData?.fbmResources || [];

  // Wait for ALL data to load before showing the calendar
  // This prevents FullCalendar from having issues when data changes and ensures resources are ready
  const isLoading = isLoadingEvents || isLoadingSchedules || isLoadingTrainers || isLoadingFbmResources;

  // Combine events and schedules (handle undefined gracefully)
  const eventsAndSchedules = [...(events || []), ...(schedules || [])];

  // Events to display (hide availability schedules if filter is active)
  const baseEventsToDisplay = shouldHideSchedules ? events : eventsAndSchedules;

  // Determine the current view's date range for filtering
  const getViewDateRange = (): { viewStart: Date; viewEnd: Date } => {
    const baseDate = date || new Date();
    if (view === 'resourceTimeGridDay') {
      // Day view: only events on the current day
      return {
        viewStart: startOfDay(baseDate),
        viewEnd: endOfDay(baseDate),
      };
    } else {
      // Week/list view: events for the current week
      const weekStartsOn = (session?.firstDayOfWeek ?? 0) as 0 | 1 | 2 | 3 | 4 | 5 | 6;
      return {
        viewStart: startOfWeek(baseDate, { weekStartsOn }),
        viewEnd: endOfWeek(baseDate, { weekStartsOn }),
      };
    }
  };

  const { viewStart, viewEnd } = getViewDateRange();

  // Filter events to only those within the current view's date range
  const eventsToDisplay = baseEventsToDisplay?.filter((event) => {
    const eventStart = new Date(event.startTime);
    const eventEnd = new Date(event.endTime);
    // Event overlaps with view range if it starts before view ends AND ends after view starts
    return eventStart <= viewEnd && eventEnd >= viewStart;
  });

  // Filter by spotsRemaining if specified
  const filteredEventsToDisplay = normalizedFilters.spotsRemaining
    ? eventsToDisplay?.filter((event) => {
      if (
        normalizedFilters.spotsRemaining !== undefined &&
        normalizedFilters.spotsRemaining !== null
      ) {
        return event.data.visitsRemaining >= normalizedFilters.spotsRemaining;
      }
      return true;
    })
    : eventsToDisplay;

  // Map events to FullCalendar format with computed resourceIds
  const calendarEvents: EventInput[] | undefined = filteredEventsToDisplay?.map((event) => {
    // Compute resource IDs for this event
    const eventResourceIds: string[] = [];
    if (event.objectType === 'schedule') {
      // Add trainer/owner ID if present
      if (event.ownerId) {
        eventResourceIds.push(`user_${event.ownerId}`);
      }
      // Also add resource IDs for schedules (assets/resources)
      const scheduleResourceIds = event.resourceIds || [];
      scheduleResourceIds.forEach((resourceId: Identifier) => {
        if (resourceId) {
          eventResourceIds.push(`resource_${resourceId}`);
        }
      });
      // Mark as unassigned if no trainer and no resources
      if (!eventResourceIds.length) {
        eventResourceIds.push('unassigned');
      }
    } else {
      // For appointments/scheduled_appointments
      const ownerIds = event.ownerId || [];
      const resIds = event.resourceIds || [];

      ownerIds.forEach((userId: Identifier) => {
        if (userId) {
          eventResourceIds.push(`user_${userId}`);
        }
      });

      resIds.forEach((resourceId: Identifier) => {
        if (resourceId) {
          eventResourceIds.push(`resource_${resourceId}`);
        }
      });

      if (!ownerIds.length && !resIds.length) {
        eventResourceIds.push('unassigned');
      }
    }

    // Availability schedules should be background events
    const isAvailabilitySchedule = event.objectType === 'schedule' && event.data.type === 'available';

    // Determine if event is draggable/resizable
    // Only appointment objects are draggable (not schedule or scheduled_appointment)
    const trainerIds: Identifier[] = Array.isArray(event.ownerId)
      ? event.ownerId
      : event.ownerId
        ? [event.ownerId]
        : [];
    const isOwnEvent = trainerIds.some((id) => String(id) === String(user.id));
    const isEditable =
      event.objectType === 'appointment' &&
      (isOwnEvent || canViewOthersScheduling);
    const eventColor = resolveCalendarEventColor(event.data.color);
    const eventColorChannel = getCalendarEventColorChannel(event.data.color);

    return {
      ...event,
      color: eventColor,
      // Use unique ID combining type, objectId, and startTime to handle recurring events
      id: `${event.objectType}_${event.objectId}_${event.startTime}`,
      background: `linear-gradient(135deg, rgba(${eventColorChannel} / 0.6), rgba(${eventColorChannel} / 0.6))`,
      backgroundColor: `rgba(${eventColorChannel} / 0.6)`,
      textColor: `rgba(${eventColorChannel} / 0.6)`,
      start: event.startTime,
      end: event.endTime,
      title: event.text,
      resourceIds: eventResourceIds,
      // Drag and resize settings (only appointments are draggable)
      editable: isEditable,
      startEditable: isEditable,
      durationEditable: isEditable,
      // Constraint: can only drag within same resource columns
      constraint: isEditable ? { resourceIds: eventResourceIds } : undefined,
      ...(isAvailabilitySchedule && { display: 'background' }),
    };
  });

  // Extract resource IDs from events (matches Ember resourceIdsWithEvents)
  const getResourceIdsWithEvents = (): string[] => {
    const ids: string[] = [];
    const allEvents = filteredEventsToDisplay || [];

    allEvents.forEach((event) => {
      if (event.objectType === 'schedule') {
        // For schedules, use ownerId (at top level, not in data)
        if (event.ownerId) {
          ids.push(`user_${event.ownerId}`);
        }
        // Also add resource IDs for schedules (assets/resources)
        const scheduleResourceIds = event.resourceIds || [];
        scheduleResourceIds.forEach((resourceId: Identifier) => {
          if (resourceId) {
            ids.push(`resource_${resourceId}`);
          }
        });
        // Mark as unassigned if no trainer and no resources
        if (!event.ownerId && !scheduleResourceIds.length) {
          ids.push('unassigned');
        }
      } else {
        // For appointments/scheduled_appointments
        const ownerIds = event.ownerId || [];
        const resourceIds = event.resourceIds || [];

        ownerIds.forEach((userId: Identifier) => {
          if (userId) {
            ids.push(`user_${userId}`);
          }
        });

        resourceIds.forEach((resourceId: Identifier) => {
          if (resourceId) {
            ids.push(`resource_${resourceId}`);
          }
        });

        if (!ownerIds.length && !resourceIds.length) {
          ids.push('unassigned');
        }
      }
    });

    // Return unique IDs
    return [...new Set(ids)];
  };
  const resourceIdsWithEvents = getResourceIdsWithEvents();

  // Build resources list (matches Ember resources computed property)
  const getResources = (): CalendarResource[] => {
    // If user cannot view others' scheduling, only show current user
    if (!canViewOthersScheduling) {
      return [
        {
          id: `user_${user.id}`,
          title: getUserDisplayName(user) || 'Me',
          sortOrder: 0,
        },
      ];
    }

    const resourceList: CalendarResource[] = [];

    // Filter out trainers excluded from FBM
    const excludeFromFbm = session?.excludeFromFbm || [];
    let filteredFbmTrainers = trainers.filter(
      (trainer) => !excludeFromFbm.includes(trainer.trainerId)
    );

    // In day view, filter trainer columns based on selected filters
    if (view === 'resourceTimeGridDay') {
      // If trainer tag filter is set, only show trainers with matching tags
      if (normalizedFilters.trainerTag) {
        const selectedTags = String(normalizedFilters.trainerTag)
          .split(',')
          .map((t) => t.trim());
        filteredFbmTrainers = filteredFbmTrainers.filter((trainer) =>
          trainer.tags?.some((tag) => selectedTags.includes(tag))
        );
      }

      // If specific trainers are selected, only show those trainer columns
      else if (normalizedFilters.trainerId) {
        const selectedTrainerIds = String(normalizedFilters.trainerId)
          .split(',')
          .map((id) => String(id).trim());
        filteredFbmTrainers = filteredFbmTrainers.filter((trainer) =>
          selectedTrainerIds.includes(String(trainer.trainerId))
        );
      }
    }

    // Add trainers (unless onlyAssets filter is active)
    if (String(normalizedFilters.onlyAssets) !== 'true' && filteredFbmTrainers.length > 0) {
      const staffOrder = session?.staffOrder || [];

      filteredFbmTrainers.forEach((trainer) => {
        const sortIndex = staffOrder.indexOf(String(trainer.trainerId));
        resourceList.push({
          id: `user_${trainer.trainerId}`,
          title: trainer.name || '',
          sortOrder: sortIndex === -1 ? null : sortIndex,
        });
      });
    }

    // Add FBM resources (assets like rooms, equipment)
    // Note: locationId filtering is done in the query, but also filter here for completeness
    if (fbmResources.length > 0) {
      let filteredResources = fbmResources;

      // Filter by location if locationId filter is set
      if (normalizedFilters.locationId) {
        const locationIds = String(normalizedFilters.locationId)
          .split(',')
          .map((id) => String(id));
        filteredResources = fbmResources.filter((resource) =>
          locationIds.includes(String(resource.locationId))
        );
      }

      filteredResources.forEach((resource) => {
        resourceList.push({
          id: `resource_${resource.id}`,
          title: resource.name,
          sortOrder: null,
        });
      });
    }

    // Add "Unassigned" if there are events without a trainer/resource
    if (resourceIdsWithEvents.includes('unassigned')) {
      resourceList.push({
        id: 'unassigned',
        title: 'Unassigned',
        sortOrder: null,
      });
    }

    // Filter to only show resources with events (if noEmptyTrainer filter is active and in day view)
    let finalResources = resourceList;
    if (String(normalizedFilters.noEmptyTrainer) === 'true' && view === 'resourceTimeGridDay') {
      finalResources = resourceList.filter((resource) =>
        resourceIdsWithEvents.includes(resource.id)
      );
    }

    // Sort by sortOrder (null values go to end)
    return finalResources.sort((a, b) => {
      if (a.sortOrder === null && b.sortOrder === null) return 0;
      if (a.sortOrder === null) return 1;
      if (b.sortOrder === null) return -1;
      return a.sortOrder - b.sortOrder;
    });
  };
  const resources = getResources();

  const onClickScheduledAppointment = async (info: EventClickArg): Promise<{
    appointment: Appointment;
    scheduledAppointment?: ScheduledAppointment | null | undefined;
    scheduledMembers?: ScheduledMembership[] | null | undefined;
  }> => {
    const eventData = info.event.extendedProps.data || {};
    const scheduledAppointment = await fetchScheduledAppointment(info.event.extendedProps.objectId);

    // This is a virtual slot from a recurring series - no appointment exists yet
    // We need to CREATE the appointment first (matching v2 editScheduledEvent behavior)
    const scheduledAppointmentId = info.event.extendedProps.objectId;

    // Create the appointment for this specific date/time slot
    // This also triggers create_member_visits on the backend
    const startTime = info.event.start ? new Date(info.event.start) : new Date();
    const endTime = info.event.end ? new Date(info.event.end) : new Date(startTime.getTime() + 60 * 60 * 1000);

    const appointment = await createAppointment({
      scheduledAppointmentId,
      serviceId: eventData.serviceId || eventData.service_id,
      locationId: eventData.locationId || eventData.location_id,
      primaryTrainerIds: info.event.extendedProps.primaryTrainerIds,
      secondaryTrainerIds: info.event.extendedProps.secondaryTrainerIds,
      startTime,
      endTime,
      description: eventData.description,
      notes: eventData.notes,
      rescheduleDate: startTime.toISOString().split('T')[0],
    } as Partial<Appointment>);

    const scheduledMembers = await fetchScheduledMemberships({
      scheduledAppointmentId: scheduledAppointment.id,
      appointmentId: appointment.id,
      fetchAll: true,
    });

    return {
      appointment,
      scheduledAppointment,
      scheduledMembers: scheduledMembers.data,
    }
  };

  const onClickAppointment = async (info: EventClickArg): Promise<{
    appointment: Appointment;
    scheduledAppointment: ScheduledAppointment | null | undefined;
    scheduledMembers: ScheduledMembership[] | null | undefined;
  }> => {
    const objectId = info.event.extendedProps.objectId;
    let scheduledAppointment: ScheduledAppointment | null | undefined = undefined;
    let scheduledMembers: ScheduledMembership[] | null | undefined = undefined;

    // This is an existing appointment - fetch it directly
    const appointment = await fetchAppointment(objectId);

    // If it's a recurring session, also fetch scheduled appointment and members
    if (appointment.scheduledAppointmentId) {
      const [scheduledAppt, membersResponse] = await Promise.all(
        [
          fetchScheduledAppointment(
            appointment.scheduledAppointmentId
          ),
          fetchScheduledMemberships({
            scheduledAppointmentId:
              appointment.scheduledAppointmentId,
            appointmentId: appointment.id,
            fetchAll: true,
          }),
        ]
      );
      scheduledAppointment = scheduledAppt as ScheduledAppointment;
      scheduledMembers = membersResponse.data as ScheduledMembership[];
    }

    return {
      appointment,
      scheduledAppointment,
      scheduledMembers
    }
  };

  const onClickSchedule = async (info: EventClickArg): Promise<{
    schedule: ScheduleItem;
    eventStartTime: Date;
  }> => {
    const objectId = info.event.extendedProps.objectId;
    const eventStart = info.event.start || new Date();
    const eventEnd = info.event.end || new Date(eventStart.getTime() + 60 * 60 * 1000);

    // Use clicked position within event to determine slot, then snap to slot start
    let clickedTime = eventStart;
    if (info.el && info.jsEvent) {
      const rect = info.el.getBoundingClientRect();
      const height = rect.height;
      if (height > 0) {
        const relativeY = Math.max(0, Math.min(1, (info.jsEvent.clientY - rect.top) / height));
        const startMs = eventStart.getTime();
        const endMs = eventEnd.getTime();
        const durationMs = endMs - startMs;
        clickedTime = new Date(startMs + durationMs * relativeY);
      }
    }

    const slotMinutes = session?.calendarSlotLength ?? 30;
    const slotMs = slotMinutes * 60 * 1000;
    const dayStart = startOfDay(clickedTime);
    const msSinceMidnight = clickedTime.getTime() - dayStart.getTime();
    const slotsFromMidnight = Math.floor(msSinceMidnight / slotMs);
    const eventStartTime = new Date(dayStart.getTime() + slotsFromMidnight * slotMs);

    const schedule = await fetchSchedule(objectId);

    return {
      schedule,
      eventStartTime,
    };
  };

  return {
    next,
    prev,
    setView,
    setDateToToday,
    goToDate,
    dateIsToday,
    view,
    title,
    date,
    formattedDate,
    calendarRef,
    handleDatesSet,
    refreshCalendar,
    onClickScheduledAppointment,
    onClickAppointment,
    onClickSchedule,
    // Data and resources
    events,
    schedules,
    eventsAndSchedules,
    eventsToDisplay,
    calendarEvents,
    resources,
    resourceIdsWithEvents,
    isLoading,
    // Normalized filter values (with "0" and "false" treated as blank)
    normalizedFilters,
    // Effective trainer IDs being used (includes default to current user)
    effectiveTrainerIds,
    // Platform settings for calendar
    firstDay: session?.firstDayOfWeek ?? 0,
    scrollTime: session?.showScheduleTime ? `${session.showScheduleTime}:00` : '05:00:00',
    slotDuration: session?.calendarSlotLength ? `00:${session.calendarSlotLength}:00` : '00:30:00',
    // Flag to indicate if trainer tag filter is active (hides trainerId filter)
    hasTrainerTagFilter: !!normalizedFilters.trainerTag,
  };
};
